@if (auth.loading()) {
  <div class="flex items-center justify-center h-screen bg-gray-100">
    <div class="flex flex-col items-center gap-4">
      <div class="h-10 w-10 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
      <span class="text-sm text-gray-500">Betöltés...</span>
    </div>
  </div>
} @else {
<div class="flex flex-col h-screen">
  <!-- Top header -->
  <header class="h-14 bg-gray-900 border-b border-gray-700 flex items-center justify-between px-4 shrink-0 z-50">
    <div class="flex items-center gap-3">
      <button (click)="toggleSidebar()" data-testid="hamburger-btn" class="p-1.5 text-gray-400 hover:text-white transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <a routerLink="/dashboard" data-testid="logo" class="flex items-center">
        <img src="logo.png" alt="WNT" class="h-8 w-auto" />
      </a>
      <span class="text-xs font-medium tracking-widest text-gray-500 uppercase hidden sm:inline">erp</span>
    </div>
    <div class="flex items-center gap-4">
      @if (isMock) {
        <div class="relative">
          <button
            (click)="mockRoleSwitcherOpen.set(!mockRoleSwitcherOpen())"
            data-testid="mock-role-switcher"
            class="bg-gray-800 text-yellow-400 text-xs border border-gray-600 rounded px-2 py-1 cursor-pointer hover:border-yellow-400 transition-colors max-w-48 truncate"
          >
            Roles: {{ activeRoles().join(', ') }}
          </button>
          @if (mockRoleSwitcherOpen()) {
            <div class="fixed right-2 top-14 sm:absolute sm:right-0 sm:top-8 bg-gray-800 border border-gray-600 rounded shadow-xl p-2 z-50 min-w-40 max-h-64 overflow-y-auto">
              @for (r of allRoles; track r.value) {
                <label class="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-gray-700 rounded"
                  [class]="activeRoles().includes(r.value) ? 'text-yellow-400' : 'text-gray-400'"
                >
                  <input
                    type="checkbox"
                    [checked]="activeRoles().includes(r.value)"
                    (change)="toggleMockRole(r.value)"
                    class="accent-yellow-400"
                  />
                  {{ r.label }}
                </label>
              }
            </div>
          }
        </div>
      }
      <app-notification-bell />
      <div class="hidden sm:flex items-center gap-3 text-sm">
        <a routerLink="/my-profile" data-testid="user-name" class="flex items-center gap-1.5 text-gray-300 hover:text-white transition-colors cursor-pointer">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          {{ auth.user()?.display_name || auth.user()?.email }}
        </a>
        <button (click)="logout()" class="text-gray-500 hover:text-white transition-colors">
          Kijelentkezés
        </button>
      </div>
    </div>
  </header>

  <!-- Below header: sidebar + content -->
  <div class="flex flex-1 overflow-hidden relative">

    <!-- Mobile overlay backdrop -->
    @if (sidebarOpen() && !isDesktop()) {
      <div (click)="closeSidebar()" class="fixed inset-0 top-14 bg-black/40 z-30"></div>
    }

    <!-- Sidebar -->
    @if (sidebarOpen()) {
      <aside data-testid="sidebar"
        [class]="isDesktop()
          ? 'w-56 bg-gray-900 text-white border-r border-gray-700 overflow-y-auto shrink-0 flex flex-col'
          : 'fixed top-14 left-0 bottom-0 w-56 bg-gray-900 text-white border-r border-gray-700 overflow-y-auto z-40 shadow-xl flex flex-col'"
      >
        <nav class="p-2 flex-1">
          @for (item of visibleNavItems(); track item.path) {
            <a
              [routerLink]="item.path"
              routerLinkActive="bg-gray-700"
              (click)="closeSidebarOnMobile()"
              class="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors whitespace-nowrap"
            >
              <span>{{ item.label }}</span>
            </a>
          }

          <!-- Mobile-only: user info + logout -->
          <div class="sm:hidden mt-4 pt-4 border-t border-gray-700 px-4">
            <a routerLink="/my-profile" (click)="closeSidebarOnMobile()" class="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {{ auth.user()?.display_name || auth.user()?.email }}
            </a>
            <button (click)="logout()" class="text-sm text-gray-500 hover:text-white mt-6 transition-colors">
              Kijelentkezés
            </button>
          </div>
        </nav>
        <div class="px-4 py-3 text-center">
          <span class="text-xs text-gray-600" data-testid="app-version">{{ version }}</span>
        </div>
      </aside>
    }

    <!-- Main content -->
    <main class="flex-1 overflow-auto bg-gray-100 pb-16 sm:pb-8">
      <div class="px-8 pt-4">
        <app-breadcrumb />
      </div>
      <router-outlet />
    </main>
  </div>
</div>

<app-toast-container />
}
